<template>
    <div data-test-view="billing">
        <l-headline
            :title="$t(`adminSettings.billingHeadlineTitle`)"
            :sub-title="$t(`adminSettings.billingHeadlineSubtitle`)"
        />

        <l-space :size="1" />

        <div class="row">
            <div class="col-md-4">
                <l-input-text
                    ref="input-receipt-email"
                    v-model="billing.receiptEmail"
                    :label="$t(`adminSettings.billingReceiptEmailLabel`)"
                    data-test-id="team-settings-billing-receipt-email"
                />
            </div>
            <div class="col-md-4">
                <l-input-text
                    ref="input-receipt-name"
                    v-model="billing.receiptName"
                    :label="$t(`adminSettings.billingReceiptNameLabel`)"
                    data-test-id="team-settings-billing-receipt-name"
                />
            </div>
            <div class="col-md-4">
                <l-input-text
                    ref="input-tax-id"
                    v-model="billing.taxId"
                    :label="$t(`adminSettings.billingTaxIdLabel`)"
                    data-test-id="team-settings-billing-tax-id"
                />
            </div>
        </div>

        <l-space :size="1" />

        <div class="row">
            <div class="col-md-3">
                <l-input-text
                    ref="input-receipt-street"
                    v-model="billing.receiptStreet"
                    :label="$t(`adminSettings.billingReceiptStreetLabel`)"
                    data-test-id="team-settings-billing-receipt-street"
                />
            </div>
            <div class="col-md-3">
                <l-input-text
                    ref="input-receipt-zip"
                    v-model="billing.receiptZIP"
                    :label="$t(`adminSettings.billingReceiptZIPLabel`)"
                    data-test-id="team-settings-billing-receipt-zip"
                />
            </div>
            <div class="col-md-3">
                <l-input-text
                    ref="input-receipt-city"
                    v-model="billing.receiptCity"
                    :label="$t(`adminSettings.billingReceiptCityLabel`)"
                    data-test-id="team-settings-billing-receipt-city"
                />
            </div>
            <div class="col-md-3">
                <l-select
                    :label="$t(`adminSettings.billingReceiptCountryLabel`)"
                    :options="countries.map(c => ({ value: c.code, text: c.name }))"
                    :value="billing.receiptCountry"
                    data-test-id="team-settings-billing-receipt-country"
                    @change="e => changeBillingCountry(e.target.value)"
                />
            </div>
        </div>

        <hr />

        <div class="row">
            <div v-if="billing.selfService" class="col-md-2" style="margin-top: 30px">
                <l-select
                    :label="$t(`adminSettings.billingTermLabel`)"
                    :options="optionsBillingCycle"
                    :value="billing.term"
                    data-test-id="team-settings-billing-cycle"
                    @change="e => selectBillingCycle({ term: e.target.value })"
                />
            </div>
            <div class="col-md-4" style="margin-top: 30px">
                <l-select
                    :label="$t(`adminSettings.billingPaymentMethod`)"
                    :options="billing.paymentMethodOptions"
                    :value="billing.paymentMethod"
                    data-test-id="team-settings-billing-payment-method"
                    @change="e => selectPaymentMethod({ method: e.target.value })"
                />
            </div>
            <div v-show="billing.paymentMethod === 'card'" class="col-md-6">
                <div class="form-row">
                    <label for="card-element" class="control-label" style="margin-bottom: 15px;">
                        {{ $t(`adminSettings.billingPaymentMethodCardElementLabel`) }}
                    </label>
                    <div id="card-element">
                        <!-- a Stripe Element will be inserted here. -->
                    </div>

                    <!-- Used to display Element errors -->
                    <div id="card-errors" role="alert"></div>
                </div>
            </div>
            <div v-show="billing.paymentMethod === 'directDebit'" class="col-md-6">
                <div class="form-row">
                    <label for="iban-element" class="control-label" style="margin-bottom: 15px;">
                        {{ $t(`adminSettings.billingPaymentMethodDirectDebitElementLabel`) }}
                    </label>
                    <div id="iban-element">
                        <!-- A Stripe Element will be inserted here. -->
                    </div>
                </div>

                <!-- Used to display form errors. -->
                <div id="directDebit-errors" role="alert"></div>
            </div>
        </div>

        <hr />

        <div class="row">
            <div class="col-md-4">
                <h4 class="color-dark">
                    <strong>
                        {{ billing.invoiceAmount }}
                        {{ billing.currency.toUpperCase() }}
                        <span v-show="!billing.taxExempted">
                            {{ $t(`adminSettings.billingPricingTaxText`) }}
                        </span>
                    </strong>
                </h4>

                <p>{{ $t(`adminSettings.billingAmountLabel`) }}</p>
            </div>
            <div v-show="billing.discountPercentage" class="col-md-4">
                <h4 class="color-dark">
                    <strong>
                        {{
                            (
                                (billing.price *
                                    billing.quantity *
                                    billing.termMultiplier *
                                    billing.discountPercentage) /
                                100
                            ).toFixed(2)
                        }}
                        {{ billing.currency.toUpperCase() }}
                    </strong>
                </h4>

                <p>
                    {{
                        $t(`adminSettings.billingSavingsLabel`, {
                            percentage: billing.discountPercentage,
                        })
                    }}
                </p>
            </div>
            <div class="col-md-4">
                <h4 class="color-dark">
                    <strong>
                        <span v-show="billing.term == 'annually'">
                            {{ $t(`adminSettings.billingTermAnnually`) }}
                        </span>
                        <span v-show="billing.term == 'quarterly'">
                            {{ $t(`adminSettings.billingTermQuarterly`) }}
                        </span>
                        <span v-show="billing.term == 'semiannually'">
                            {{ $t(`adminSettings.billingTermSemianually`) }}
                        </span>
                        <span v-show="billing.term == 'monthly'">
                            {{ $t(`adminSettings.billingTermMonthly`) }}
                        </span>
                    </strong>
                </h4>

                <p>{{ $t(`adminSettings.billingTermLabel`) }}</p>
            </div>
        </div>

        <hr />

        <p>
            <a href="https://www.leapsome.com/terms" target="_blank" rel="noopener">{{
                $t(`adminSettings.billingTermsLabel`)
            }}</a>
            <l-text
                :text="
                    $t(`adminSettings.billingPricingExplanationText`, {
                        users: billing.quantity,
                        currency: billing.currency.toUpperCase(),
                        amountMonth: billing.effectivePrice,
                        amountYear: (billing.effectivePrice * 12).toFixed(2),
                    })
                "
            />
        </p>

        <!-- Display mandate acceptance text. -->
        <p v-show="billing.paymentMethod === 'directDebit'" id="mandate-acceptance">
            {{ $t(`adminSettings.billingMandateAcceptanceText`) }}
        </p>

        <hr />

        <div class="row">
            <div class="col-md-12">
                <button
                    class="btn btn-primary btn-raised btn-block"
                    :disabled="updateBillingInProgress"
                    data-test-id="team-settings-billing-update"
                    @click="() => updateBilling()"
                >
                    <i
                        :class="[
                            'fa',
                            { 'fa-lock': !updateBillingInProgress },
                            { 'fa-refresh fa-spin': updateBillingInProgress },
                        ]"
                    ></i>

                    <span v-show="!updateBillingInProgress">
                        {{ $t(`adminSettings.billingUpdateBtn`) }}
                    </span>
                    <span v-show="updateBillingInProgress">
                        {{ $t(`adminSettings.billingUpdateInProgressBtn`) }}
                    </span>
                </button>
            </div>
        </div>

        <hr />

        <l-text-alert
            v-show="updateBillingInProgress"
            type="info"
            :text="$t(`adminSettings.billingUpdateInProgressText`)"
        />
        <l-text-alert
            v-show="updateBillingSuccess"
            type="success"
            :text="$t(`adminSettings.billingUpdateSuccessText`)"
            data-test-id="team-settings-billing-update-success"
        />
        <l-text-alert
            v-show="updateBillingError"
            type="warning"
            :text="updateBillingErrorMessage"
            data-test-id="team-settings-billing-update-error"
        />

        <l-space :size="1" />

        <!-- INVOICES -->
        <l-headline
            :title="$t(`adminSettings.billingInvoicesHeadlineTitle`)"
            :sub-title="$t(`adminSettings.billingInvoicesHeadlineSubtitle`)"
        />

        <div
            v-if="
                billing.stripeSource &&
                    billing.stripeSource.mandate &&
                    billing.stripeSource.mandate.url
            "
        >
            <a :href="billing.stripeSource.mandate.url" target="_blank" rel="noopener">
                {{ $t(`adminSettings.billingInvoicesStripeMandateLabel`) }}
            </a>

            <l-space :size="2" />
        </div>

        <div v-for="invoice in invoices" :key="invoice.id" class="row">
            <div class="col-md-4">
                <a :href="invoice.url" target="_blank" rel="noopener">
                    {{
                        $t(`adminSettings.billingInvoicesInvoiceLabel`, { number: invoice.number })
                    }}
                </a>
            </div>
            <div class="col-md-3">
                <span class="pull-right">
                    {{ invoice.total }} {{ billing.currency.toUpperCase() }}
                </span>
            </div>
            <div class="col-md-3">
                <span class="pull-right">
                    {{ invoice.date }}
                </span>
            </div>
            <div class="col-md-1">
                <span v-show="invoice.paid" class="pull-right color-success">
                    <i
                        v-b-popover.hover.top="{
                            content: $t('adminSettings.billingInvoicesPaidLabel'),
                        }"
                        class="fa fa-check"
                    ></i>
                </span>
            </div>
            <div class="col-md-1">
                <span class="pull-right">
                    <a :href="invoice.pdf" target="_blank" rel="noopener">
                        <i
                            v-b-popover.hover.top="{
                                content: $t('adminSettings.billingInvoicesDownloadLabel'),
                            }"
                            class="fa fa-download"
                        ></i>

                        <!-- Content needed for accessibility -->
                        <span style="display:none;">Download</span>
                    </a>
                </span>
            </div>
        </div>

        <div v-show="!invoices.length" class="color-grey">
            {{ $t(`adminSettings.billingInvoicesNoData`) }}
        </div>

        <hr />

        <l-headline :title="$t(`adminSettings.billingDataProcessingAgreement`)" />

        <i18n path="adminSettings.billingDataProcessingAgreementHint" for="value">
            <b>{{ $t(`adminSettings.billingDataProcessingAgreement`) }}</b>
        </i18n>

        <l-space :size="1" />

        <ul>
            <li>
                <a
                    href="https://docs.google.com/forms/d/e/1FAIpQLSeT4SVrw4YUCDxWxR-huXcHHXm8EVPjmyJ5wuiwAT4Ezm0N-Q/viewform"
                    target="_blank"
                    rel="noopener noreferrer"
                    >{{ $t(`adminSettings.billingRequestEnglishDataProcessingAgreement`) }}</a
                >
            </li>
            <li>
                <a
                    href="https://docs.google.com/forms/d/e/1FAIpQLSeu7D0SxsxPw2oR1JmRwJzvpUlKFWSFVvOrY-PX7nKoCDxCFA/viewform"
                    target="_blank"
                    rel="noopener noreferrer"
                    >{{ $t(`adminSettings.billingRequestGermanDataProcessingAgreement`) }}</a
                >
            </li>
        </ul>
    </div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";
import { mapGetters } from "vuex";

import countries from "@/config/countries";

import LHeadline from "@/components/LHeadline";
import LSpace from "@/components/LSpace";
import LInputText from "@/components/LInputText";
import LSelect from "@/components/LSelect";
import LText from "@/components/LText";
import LTextAlert from "@/components/LTextAlert";

export default {
    components: {
        LHeadline,
        LSpace,
        LInputText,
        LSelect,
        LText,
        LTextAlert,
    },
    data() {
        return {
            updateBillingInProgress: false,
            updateBillingSuccess: false,
            updateBillingError: false,
            updateBillingErrorMessage: "",

            // Billing defaults, will be overriden in `created()` with team specific data
            billing: {
                receiptCountry: "US",
                price: 7,
                annually: true,
                termsAccepted: false,
                paymentMethod: "card",
            },
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            getTeamInfo: "teams/current/info",
            invoices: "teams/current/invoices",
            invoiceQuantity: "teams/current/invoiceQuantity",
        }),
        optionsBillingCycle() {
            return [
                {
                    value: "annually",
                    text: this.$t(`adminSettings.billingTermAnnually`),
                },
                {
                    value: "monthly",
                    text: this.$t(`adminSettings.billingTermMonthly`),
                },
            ];
        },
    },
    created() {
        this.countries = countries;

        // Cloning the information from the store so we can modify it before updating the store,
        // when the user clicks on the update button.
        //
        this.team = this.getTeamInfo();
        this.billing = {
            ...this.billing,
            ...this.team.billing,
            quantity: this.invoiceQuantity,
        };

        this.getEffectivePrice();
        this.getBillingCycle();
        this.getInvoiceAmount();
        this.getPaymentMethodOptions();
        this.changeBillingCountry(this.billing.receiptCountry);
    },
    async mounted() {
        // Insert Stripe elements directly into the DOM
        //

        // Initialise Stripe
        const key =
            window.location.host === "www.leapsome.com"
                ? // LIVE
                  "pk_live_OH4MX4p7dFl9Q6HqxJmgL7Fl"
                : // TEST
                  "pk_test_tXdvzGU0jZ6HPQVOgsPCFDwB";
        this.stripe = await loadStripe(key);

        const elements = this.stripe.elements();

        const style = {
            base: {
                fontSize: "16px",
                color: "#32325d",
            },
        };

        // Card payments
        this.card = elements.create("card", {
            style,
        });
        this.card.mount("#card-element");

        this.card.addEventListener("change", function(event) {
            const displayError = document.getElementById("card-errors");
            if (event.error) {
                displayError.textContent = event.error.message;
            } else {
                displayError.textContent = "";
            }
        });

        // Direct debit payments
        this.iban = elements.create("iban", {
            style,
            supportedCountries: ["SEPA"],
            placeholderCountry: "DE",
        });
        this.iban.mount("#iban-element");

        this.iban.addEventListener("change", function(event) {
            const displayError = document.getElementById("directDebit-errors");
            if (event.error) {
                displayError.textContent = event.error.message;
            } else {
                displayError.textContent = "";
            }
        });
    },
    methods: {
        changeBillingCountry(country) {
            if (!country || !countries.find(c => c.code === country)) {
                return;
            }

            // Defaults - USD pricing

            this.$set(this.billing, "taxExmpted", true);
            this.$set(this.billing, "paymentMethod", "card");
            this.$set(this.billing, "receiptCountry", country);

            // Currency

            if (!this.billing.overrideAutoCurrency) {
                if (countries.find(c => c.code === country).isEuro) {
                    this.$set(this.billing, "currency", "eur");
                } else {
                    this.$set(this.billing, "currency", "usd");
                }
            }

            // Euro pricing

            if (countries.find(c => c.code === country).isEuro) {
                this.$set(this.billing, "paymentMethod", "directDebit");
            }

            // German companies need to pay taxes

            if (country === "DE") {
                this.$set(this.billing, "taxExempted", false);
            }

            this.getEffectivePrice();
            this.getPaymentMethodOptions();
            this.getInvoiceAmount();
        },
        getEffectivePrice() {
            var effPrice;
            var discountPercentage;
            var coupon = this.billing.coupon;
            var price = this.billing.price;

            if (!coupon || coupon == "none") {
                effPrice = price * 1;
                discountPercentage = 0;
            } else if (coupon.startsWith("5P")) {
                effPrice = price * 0.95;
                discountPercentage = 5;
            } else if (coupon.startsWith("10P")) {
                effPrice = price * 0.9;
                discountPercentage = 10;
            } else if (coupon.startsWith("15P")) {
                effPrice = price * 0.85;
                discountPercentage = 15;
            } else if (coupon.startsWith("20P")) {
                effPrice = price * 0.8;
                discountPercentage = 20;
            } else if (coupon.startsWith("25P")) {
                effPrice = price * 0.75;
                discountPercentage = 25;
            } else if (coupon.startsWith("30P")) {
                effPrice = price * 0.7;
                discountPercentage = 30;
            } else if (coupon.startsWith("35P")) {
                effPrice = price * 0.65;
                discountPercentage = 35;
            } else if (coupon.startsWith("40P")) {
                effPrice = price * 0.6;
                discountPercentage = 40;
            } else if (coupon.startsWith("45P")) {
                effPrice = price * 0.55;
                discountPercentage = 45;
            } else if (coupon.startsWith("50P")) {
                effPrice = price * 0.5;
                discountPercentage = 50;
            }

            this.$set(this.billing, "effectivePrice", effPrice.toFixed(2));
            this.$set(this.billing, "discountPercentage", discountPercentage);
        },
        getBillingCycle() {
            if (this.billing.term == "annually") {
                this.$set(this.billing, "termMultiplier", 12);
            } else if (this.billing.term == "quarterly") {
                this.$set(this.billing, "termMultiplier", 3);
            } else if (this.billing.term == "semiannually") {
                this.$set(this.billing, "termMultiplier", 6);
            } else {
                this.$set(this.billing, "termMultiplier", 1);
            }
        },
        getInvoiceAmount() {
            this.$set(
                this.billing,
                "invoiceAmount",
                (
                    this.billing.effectivePrice *
                    this.billing.quantity *
                    this.billing.termMultiplier
                ).toFixed(2),
            );
        },
        getPaymentMethodOptions() {
            this.$set(this.billing, "paymentMethodOptions", []);

            if (this.billing.currency === "eur") {
                this.billing.paymentMethodOptions.push({
                    value: "directDebit",
                    text: this.$t(`adminSettings.billingPaymentMethodDirectDebitLabel`),
                });
            }

            if (this.billing.invoiceAmount > 20000) {
                this.billing.paymentMethodOptions.push({
                    value: "invoice",
                    text: this.$t(`adminSettings.billingPaymentMethodInvoiceLabel`),
                });
            } else {
                this.billing.paymentMethodOptions.push({
                    value: "card",
                    text: this.$t(`adminSettings.billingPaymentMethodCardLabel`),
                });
            }

            this.$set(this.billing, "paymentMethod", this.billing.paymentMethodOptions[0]?.value);
        },
        selectBillingCycle({ term }) {
            if (!this.billing.selfService) {
                return;
            }

            if (term === "monthly") {
                this.$set(this.billing, "coupon", "none");
            } else {
                this.$set(this.billing, "coupon", "10P-FOREVER");
            }

            this.$set(this.billing, "term", term);

            this.getBillingCycle();
            this.getEffectivePrice();
            this.getInvoiceAmount();
            this.getPaymentMethodOptions();
        },
        selectPaymentMethod({ method }) {
            this.$set(this.billing, "paymentMethod", method);
        },
        updateBilling() {
            this.updateBillingInProgress = true;
            this.updateBillingSuccess = false;
            this.updateBillingError = false;
            this.updateBillingErrorMessage = "";

            const errorMessage = this.$t(`adminSettings.billingUpdateErrorMessageGeneric`);

            // Get token from Stripe
            const input = this.billing.paymentMethod === "card" ? this.card : this.iban;
            const sourceData =
                this.billing.paymentMethod === "card"
                    ? {}
                    : {
                          type: "sepa_debit",
                          currency: "eur",
                          owner: {
                              name: this.billing.receiptName,
                              email: this.billing.receiptEmail,
                          },
                          mandate: {
                              // Automatically send a mandate notification email to your customer once the source is charged.
                              notification_method: "email",
                          },
                      };

            if (this.billing.paymentMethod === "invoice") {
                // No Stripe interaction required in the frontend, update the team with new billing details

                return this.updateGlobalTeam()
                    .then(() => {
                        // Get update team info (potentially with `stripeClientSecret`)
                        this.team = this.getTeamInfo();
                        this.updateBillingSuccess = true;

                        // Refresh token
                        return this.$store.dispatch({
                            type: "users/current/refreshToken",
                            payload: { force: true },
                        });
                    })
                    .catch(err => {
                        this.updateBillingError = true;
                        this.updateBillingErrorMessage =
                            err?.response?.data?.message ?? errorMessage;
                    })
                    .finally(() => {
                        this.updateBillingInProgress = false;
                    });
            }

            this.stripe
                .createSource(input, sourceData)
                .then(result => {
                    if (result.error) {
                        document.getElementById(
                            `${this.billing.paymentMethod}-errors`,
                        ).textContent = result.error.message;

                        this.updateBillingInProgress = false;
                    } else {
                        this.billing.stripeToken = result.source;

                        // Update the team with new billing details
                        return this.updateGlobalTeam()
                            .then(() => {
                                // Get update team info (potentially with `stripeClientSecret`)
                                this.team = this.getTeamInfo();

                                // Handle card payments that require additional authentication
                                if (this.team.stripeClientSecret) {
                                    return this.stripe
                                        .confirmCardPayment(this.team.stripeClientSecret)
                                        .then(result => {
                                            if (result.error) {
                                                // Display error.message in your UI.
                                                this.updateBillingError = true;
                                                this.updateBillingErrorMessage =
                                                    result.error.message;
                                            } else {
                                                // The payment has succeeded. Display a success message.
                                                this.updateBillingSuccess = true;

                                                // Refetch invoices
                                                this.$store.dispatch({
                                                    type: "teams/current/getInvoices",
                                                });
                                            }
                                        });
                                } else {
                                    this.updateBillingSuccess = true;
                                }
                            })
                            .then(() => {
                                // Refresh token
                                return this.$store.dispatch({
                                    type: "auth/performSessionRefresh",
                                });
                            })
                            .finally(() => {
                                this.updateBillingInProgress = false;
                            });
                    }
                })
                .catch(err => {
                    this.updateBillingError = true;
                    this.updateBillingErrorMessage = err?.response?.data?.message ?? errorMessage;
                });
        },
        isValid() {
            const inputs = Object.keys(this.$refs)
                .map(key => (key.startsWith("input-") ? this.$refs[key] : null))
                .filter(Boolean);

            return inputs.reduce((a, c) => c.validate() && a, true);
        },
        updateGlobalTeam() {
            const errorMessage = this.$t(`adminSettings.billingUpdateErrorMessageGeneric`);

            if (!this.isValid()) {
                throw new Error(errorMessage);
            }

            return this.$store.dispatch({
                type: "teams/current/update",
                payload: {
                    scope: "billing",
                    update: {
                        ...this.billing,
                    },
                },
            });
        },
    },
};
</script>

<style scoped></style>
